import { FieldInputProps, FormikProps, useFormikContext } from 'formik';
import { InputMessage } from 'plume-ui/dist/components/InputField/InputField';
import { Maybe } from '../../../../../types';
import validateEmail from '../../../../../utils/isEmail/validateEmail';

export type SessionCompletionPredicate<T> = {
  fieldName: keyof T;
  validation: 'required' | 'email';
};

export type FormControlsState<T> = {
  getPropertyValue: (propertyName: keyof T) => T[keyof T];
  setPropertyValue: (propertyName: keyof T, value: Maybe<T[keyof T]>) => void;
};

export type UseFormControlsValues<T> = {
  toggleFieldValue: (propertyName: keyof T) => void;
  renderErrorMessage: (propertyName: keyof T) => InputMessage[];
  isSectionComplete: (
    requiredFields: SessionCompletionPredicate<T>[],
  ) => boolean;
} & FormControlsState<T>;

function useFormControls<T>(
  field: FieldInputProps<T>,
  form: FormikProps<T>,
): UseFormControlsValues<T> {
  const { errors } = useFormikContext();
  const toggleFieldValue = (propertyName: keyof T) => {
    form.setFieldValue(field.name, {
      ...field.value,
      [propertyName]: !field.value[propertyName],
    });
  };

  const getPropertyValue = (propertyName: keyof T): T[keyof T] => {
    return field.value[propertyName];
  };

  const setPropertyValue = (property: keyof T, value: Maybe<T[keyof T]>) => {
    form.setFieldValue(field.name, {
      ...field.value,
      [property]: value,
    });
  };

  const renderErrorMessage = (propertyName: keyof T): InputMessage[] => {
    const sectionName = field.name;
    const section = (errors as any)![sectionName];
    if (!section) {
      return [];
    }
    const errorsFromProperty = section[propertyName];
    if (!Array.isArray(errorsFromProperty)) {
      return [
        {
          status: 'error',
          message: errorsFromProperty,
        },
      ];
    }
    return errorsFromProperty.map((m: string) => ({
      status: 'error',
      message: m,
    }));
  };

  const isSectionComplete = (
    requiredProps: SessionCompletionPredicate<T>[],
  ): boolean => {
    const positivelyValidatedFields = requiredProps
      .map((p) => {
        const value = field.value[p.fieldName];
        const valueToCompare = Array.isArray(value) ? value[0] : value;
        return p.validation === 'required'
          ? Boolean(valueToCompare)
          : validateEmail(valueToCompare);
      })
      .filter((v) => !!v);
    return positivelyValidatedFields.length === requiredProps.length;
  };

  return {
    toggleFieldValue,
    getPropertyValue,
    setPropertyValue,
    renderErrorMessage,
    isSectionComplete,
  };
}

export default useFormControls;
